import { useEffect, useState } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import ScrollToTop from '../../../components/feature/ScrollToTop';
import QuoteFormSection from '../../home/components/QuoteFormSection';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { useSEO } from '../../../utils/seo';

const ProteccionesAcabadosPage = () => {
  const [images, setImages] = useState<string[]>([]);
  const [videos, setVideos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; type: 'image' | 'video' } | null>(null);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hormipremium.com';

  useSEO({
    title: 'Protecciones y Acabados Estepona | Sellados Premium HORMI PREMIUM',
    description: 'Especialistas en protecciones y acabados para hormigón en Estepona. Sellados, resinas y tratamientos que prolongan la vida útil de tus pavimentos.',
    keywords: 'sellado hormigón Estepona, protección pavimentos, resinas hormigón, acabados premium',
    canonical: `${siteUrl}/servicios/protecciones-acabados`,
  });

  useEffect(() => {
    loadMedia();
    setupScrollAnimations();
  }, []);

  const setupScrollAnimations = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  };

  const loadMedia = async () => {
    try {
      const { data: files, error } = await supabase.storage
        .from('HormiPremium')
        .list('Proteccion', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' },
        });

      if (error) {
        console.error('Error loading media:', error);
        setLoading(false);
        return;
      }

      if (files && files.length > 0) {
        const imageFiles: string[] = [];
        const videoFiles: string[] = [];

        files
          .filter((file) => file.name !== '.emptyFolderPlaceholder' && file.id)
          .forEach((file) => {
            const { data } = supabase.storage
              .from('HormiPremium')
              .getPublicUrl(`Proteccion/${file.name}`);
            
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (extension && ['mp4', 'webm', 'mov'].includes(extension)) {
              videoFiles.push(data.publicUrl);
            } else {
              imageFiles.push(data.publicUrl);
            }
          });

        setImages(imageFiles);
        setVideos(videoFiles);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Protección Duradera',
      description: 'Sellados que protegen contra manchas, humedad y desgaste',
    },
    {
      icon: 'ri-drop-line',
      title: 'Impermeabilización',
      description: 'Barrera efectiva contra la penetración de agua',
    },
    {
      icon: 'ri-sun-line',
      title: 'Resistencia UV',
      description: 'Protección contra la decoloración por rayos solares',
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Acabados Personalizados',
      description: 'Mate, satinado o brillante según tus preferencias',
    },
    {
      icon: 'ri-time-line',
      title: 'Prolonga la Vida Útil',
      description: 'Aumenta significativamente la durabilidad del pavimento',
    },
    {
      icon: 'ri-brush-line',
      title: 'Fácil Mantenimiento',
      description: 'Superficies más fáciles de limpiar y mantener',
    },
  ];

  const applications = [
    {
      icon: 'ri-road-map-line',
      title: 'Hormigón Impreso',
      description: 'Sellado y protección de texturas',
    },
    {
      icon: 'ri-layout-line',
      title: 'Hormigón Pulido',
      description: 'Densificadores y abrillantadores',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Hormigón Desactivado',
      description: 'Protección de áridos vistos',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Zonas de Piscina',
      description: 'Sellados resistentes al cloro',
    },
    {
      icon: 'ri-car-line',
      title: 'Garajes',
      description: 'Protección contra aceites y manchas',
    },
    {
      icon: 'ri-building-2-line',
      title: 'Fachadas',
      description: 'Hidrofugantes y protectores',
    },
  ];

  const benefits = [
    'Protección contra manchas y suciedad',
    'Impermeabilización efectiva',
    'Resistencia a productos químicos',
    'Protección UV anti-decoloración',
    'Acabados mate, satinado o brillante',
    'Prolonga la vida útil del pavimento',
    'Facilita la limpieza diaria',
    'Realza el color y la textura',
  ];

  const process = [
    {
      step: '1',
      title: 'Evaluación',
      description: 'Análisis del tipo de superficie y necesidades específicas',
    },
    {
      step: '2',
      title: 'Limpieza',
      description: 'Preparación profunda de la superficie',
    },
    {
      step: '3',
      title: 'Reparaciones',
      description: 'Corrección de imperfecciones si es necesario',
    },
    {
      step: '4',
      title: 'Aplicación',
      description: 'Extendido del sellador o resina protectora',
    },
    {
      step: '5',
      title: 'Curado',
      description: 'Tiempo de secado y endurecimiento óptimo',
    },
  ];

  const isVisible = (id: string) => visibleSections.has(id);
  const allMedia = [...images, ...videos];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-green-600 overflow-hidden"
        id="hero"
        data-animate
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className={`max-w-7xl mx-auto px-6 relative z-10 transition-all duration-1000 ${
          isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors cursor-pointer group"
              >
                <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
                <span>Volver a Servicios</span>
              </Link>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Protecciones y Acabados
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Sellados y tratamientos premium que protegen y embellecen tus pavimentos. Prolongamos la vida útil de tu inversión con productos de máxima calidad.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+34640877479"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  <span>Llamar Ahora</span>
                </a>
                <a
                  href="https://wa.me/34640877479"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {loading ? (
                <div className="col-span-2 flex items-center justify-center h-96">
                  <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                allMedia.slice(0, 4).map((media, index) => {
                  const isVideo = videos.includes(media);
                  return (
                    <div
                      key={index}
                      className={`rounded-2xl overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500 ${
                        index === 0 ? 'col-span-2' : ''
                      } ${isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                      style={{ transitionDelay: `${index * 150}ms` }}
                    >
                      <div className="h-64">
                        {isVideo ? (
                          <video
                            src={media}
                            className="w-full h-full object-cover object-top"
                            autoPlay
                            loop
                            muted
                            playsInline
                          />
                        ) : (
                          <img
                            src={media}
                            alt={`Protección ${index + 1}`}
                            className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700"
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        className="py-20 bg-gray-50"
        id="features"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Características Principales
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Protecciones profesionales para todo tipo de pavimentos
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 group ${
                  isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-dark mb-4 group-hover:text-emerald-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section 
        className="py-20 bg-white"
        id="applications"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('applications') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Aplicaciones Ideales
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Protección para todo tipo de superficies de hormigón
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applications.map((app, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-100 hover:border-emerald-500 transition-all duration-500 group ${
                  isVisible('applications') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-all duration-300">
                  <i className={`${app.icon} text-2xl text-emerald-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-emerald-600 transition-colors">
                  {app.title}
                </h3>
                <p className="text-secondary text-sm">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50"
        id="benefits"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${
              isVisible('benefits') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}>
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-8">
                Beneficios de Nuestros Tratamientos
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 ${
                      isVisible('benefits') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                    }`}
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <p className="text-dark font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {loading ? (
                <div className="col-span-2 flex items-center justify-center h-96">
                  <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                allMedia.slice(4, 8).map((media, index) => {
                  const isVideo = videos.includes(media);
                  return (
                    <div
                      key={index}
                      className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 ${
                        isVisible('benefits') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                      }`}
                      style={{ transitionDelay: `${index * 150}ms` }}
                    >
                      <div className="h-48">
                        {isVideo ? (
                          <video
                            src={media}
                            className="w-full h-full object-cover object-top"
                            autoPlay
                            loop
                            muted
                            playsInline
                          />
                        ) : (
                          <img
                            src={media}
                            alt={`Beneficio ${index + 1}`}
                            className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700"
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        className="py-20 bg-white"
        id="process"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('process') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Proceso de Aplicación
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Trabajamos con precisión en cada etapa para garantizar resultados perfectos
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item, index) => (
              <div
                key={index}
                className={`relative transition-all duration-1000 ${
                  isVisible('process') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group h-full">
                  <div className="text-5xl font-bold text-white/20 mb-4 group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-white/90 text-sm leading-relaxed">{item.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 transform -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section 
        className="py-20 bg-gray-50"
        id="gallery"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible('gallery') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
              Galería de Proyectos
            </h2>
            <p className="text-secondary text-lg max-w-3xl mx-auto">
              Descubre nuestros trabajos de protección y acabados
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-secondary">Cargando galería...</p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {allMedia.slice(8).map((media, index) => {
                const isVideo = videos.includes(media);
                return (
                  <div
                    key={index}
                    className={`break-inside-avoid group transition-all duration-700 cursor-pointer ${
                      isVisible('gallery') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                    onClick={() => setSelectedMedia({ url: media, type: isVideo ? 'video' : 'image' })}
                  >
                    <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500">
                      <div className="h-80">
                        {isVideo ? (
                          <video
                            src={media}
                            className="w-full h-full object-cover object-top"
                            autoPlay
                            loop
                            muted
                            playsInline
                          />
                        ) : (
                          <img
                            src={media}
                            alt={`Proyecto ${index + 1}`}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                          />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                          <p className="text-lg font-bold">Protecciones y Acabados</p>
                          <p className="text-sm opacity-90">Costa del Sol</p>
                        </div>
                      </div>
                      {isVideo && (
                        <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <i className="ri-play-circle-fill text-2xl text-white"></i>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-green-600 relative overflow-hidden"
        id="cta"
        data-animate
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className={`max-w-4xl mx-auto px-6 text-center relative z-10 transition-all duration-1000 ${
          isVisible('cta') ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para tu Proyecto?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Solicita tu presupuesto gratuito y sin compromiso. Nuestro equipo te asesorará en todo el proceso.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+34640877479"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>640 877 479</span>
            </a>
            <a
              href="https://wa.me/34640877479"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              <span>WhatsApp</span>
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-3 px-8 py-4 bg-emerald-700 text-white rounded-full font-semibold hover:bg-emerald-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>Formulario</span>
            </Link>
          </div>
        </div>
      </section>

      <QuoteFormSection />
      <Footer />
      <WhatsAppButton />
      <ScrollToTop />

      {/* Modal de Media */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all cursor-pointer"
            onClick={() => setSelectedMedia(null)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          {selectedMedia.type === 'video' ? (
            <video
              src={selectedMedia.url}
              className="max-w-full max-h-full rounded-lg"
              controls
              autoPlay
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={selectedMedia.url}
              alt="Vista ampliada"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProteccionesAcabadosPage;
